import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Clapperboard, PlayCircle, School, HeartHandshake, Sparkles, Building2, MonitorPlay } from "lucide-react";

const OVERVIEW = {
  key: "master-overview",
  section: "Master Overview",
  title: "MyTableTopApp Master Overview — Safe Kids Marketplace & PTA Fundraising",
  file: "MyTableTop App Explainer v5.mp4",
  src: "/videos/MyTableTop%20App%20Explainer%20v5.mp4",
  description: "The full end-to-end tour: safe kid shopfronts, parent approvals, automatic PTA splits and verified community impact.",
  Icon: MonitorPlay,
  wrap: "bg-brandSlate/10 text-brandSlate",
  featured: true,
};

const VIDEOS = [
  {
    key: "kids-families",
    section: "Overview & Getting Started · Kids & Families",
    title: "How Kids & Families Buy and Sell Safely",
    file: "MyTableTop Kids & Teens Explainer v1.mp4",
    src: "/videos/MyTableTop%20Kids%20%26%20Teens%20Explainer%20v1.mp4",
    description: "Learn how kids run their own digital shopfront under full parental supervision, earn pocket money, and divert items from landfill.",
    Icon: PlayCircle,
    wrap: "bg-brandPink/15 text-brandPink",
    span: "lg:col-span-2",
  },
  {
    key: "schools-ptas",
    section: "Schools & PTAs",
    title: "Year-Round Continuous Digital Fundraising for Schools & PTAs",
    file: "MyTableTop Schools & PTA Explainer v3.mp4",
    src: "/videos/MyTableTop%20Schools%20%26%20PTA%20Explainer%20v3.mp4",
    description: "Discover how pupils share 25%, 50%, 75%, or 100% of profits automatically with their school or PTA.",
    Icon: School,
    wrap: "bg-brandTeal/15 text-brandTeal",
    span: "lg:col-span-2",
  },
  {
    key: "charities-community",
    section: "Charities & Community Groups",
    title: "Continuous Fundraising for Charities & Local Causes",
    file: "MyTableTop Community & Charity Explainer v1.mp4",
    src: "/videos/MyTableTop%20Community%20%26%20Charity%20Explainer%20v1.mp4",
    description: "See how community groups receive automatic fundraising splits from pre-loved sales all year round.",
    Icon: HeartHandshake,
    wrap: "bg-brandPink/15 text-brandPink",
    span: "lg:col-span-2",
  },
  {
    key: "young-sellers",
    section: "Inspiration & Crafting for Young Sellers",
    title: "10 Creative Things to Make & Sell",
    file: "10 Creative Things to Make & Sell v1.mp4",
    src: "/videos/10%20Creative%20Things%20to%20Make%20%26%20Sell%20v1.mp4",
    description: "Ideas for young entrepreneurs — from friendship bracelets and painted pebbles to mini canvases.",
    Icon: Sparkles,
    wrap: "bg-brandTeal/15 text-brandTeal",
    span: "lg:col-span-2 lg:col-start-2",
  },
  {
    key: "corporate-partners",
    section: "Corporate Partners & Sponsors",
    title: "Corporate Sponsorship & Family Support Impact",
    file: "MyTableTop Corporate Sponsorship v1.mp4",
    src: "/videos/MyTableTop%20Corporate%20Sponsorship%20v1.mp4",
    description: "How corporate partner giving funds pre-loved items, populates the family support shop, and generates verified impact reports.",
    Icon: Building2,
    wrap: "bg-brandSlate/10 text-brandSlate",
    span: "lg:col-span-2 lg:col-start-4",
  },
];

function VideoCard({ video }) {
  const [failed, setFailed] = useState(false);
  const { Icon } = video;
  const featured = !!video.featured;
  return (
    <article className={`card-base p-6 md:p-8 flex flex-col ${video.span || ""}`} data-testid={`video-hub-card-${video.key}`}>
      <div className={`inline-flex items-center gap-2 self-start rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] ${video.wrap}`} data-testid={`video-hub-section-${video.key}`}>
        <Icon className="w-4 h-4" /> {video.section}
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-brandSlate mt-4" data-testid={`video-hub-title-${video.key}`}>
        {video.title}
      </h2>
      <div
        className={`mt-5 rounded-3xl overflow-hidden border border-slate-100 bg-brandSlate w-full ${featured ? "aspect-video" : "aspect-video lg:aspect-auto lg:h-[300px]"}`}
        data-testid={`video-hub-player-${video.key}`}
      >
        {failed ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-center text-white/80 px-6" data-testid={`video-hub-fallback-${video.key}`}>
            <Clapperboard className="w-8 h-8 mb-3 text-white/60" />
            <div className="font-display text-lg font-semibold text-white">{video.title}</div>
            <div className="text-xs mt-2">Video file will appear here once uploaded: <span className="font-mono">{video.file}</span></div>
          </div>
        ) : (
          <video
            controls
            playsInline
            preload="metadata"
            className={`w-full h-full ${featured ? "object-cover" : "object-cover lg:object-contain"}`}
            data-testid={`video-hub-video-${video.key}`}
            onError={() => setFailed(true)}
          >
            <source src={video.src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
      <p className="text-slate-600 mt-4 leading-relaxed" data-testid={`video-hub-description-${video.key}`}>
        {video.description}
      </p>
    </article>
  );
}

export default function VideoHub() {
  useEffect(() => {
    const prev = document.title;
    document.title = "MyTableTopApp Video Hub — How It Works for Everyone";
    window.scrollTo(0, 0);
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 lg:pt-20 pb-8" data-testid="video-hub-page">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-brandPink/15 text-brandPink font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
          data-testid="video-hub-eyebrow"
        >
          <Clapperboard className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex-shrink-0" /> Video Hub
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-semibold text-brandSlate leading-[1.08] mt-5 max-w-4xl" data-testid="video-hub-title">
          MyTableTopApp Video Hub — How It Works for Everyone
        </h1>
        <p className="text-lg text-slate-600 mt-5 max-w-3xl leading-relaxed" data-testid="video-hub-hook">
          Discover how MyTableTopApp empowers kids, supports schools, powers community fundraising, and turns corporate giving into real local impact.
        </p>

        <div className="mt-12 max-w-5xl mx-auto" data-testid="video-hub-featured">
          <VideoCard video={OVERVIEW} />
        </div>

        <div className="grid lg:grid-cols-6 gap-6 mt-8" data-testid="video-hub-grid">
          {VIDEOS.map((video) => (
            <VideoCard key={video.key} video={video} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
